import { ArrowRight, Home, Heart, DollarSign, CheckCircle, Users, Shield, TrendingUp, Building, FileText, Menu, ChevronDown, Search, Key, Star } from "lucide-react";
import { Button } from "../components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import SEO from "../components/SEO";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5ead2d97-7ad4-48d6-8ee4-b13556106f69/artifacts/jd65gb5i_CityWorth%20Logo.png";

const IMG_BROWSING = "https://static.prod-images.emergentagent.com/jobs/38ff7071-66f9-4d88-af35-8aea95a1b115/images/adf639bcdc4ac26aa941eed3a56a87ac801a7022c7aececa0f2e4eb10cf7f6a4.png";
const IMG_TEAM = "https://static.prod-images.emergentagent.com/jobs/38ff7071-66f9-4d88-af35-8aea95a1b115/images/daa824d33d3e21c2028f2a67847cdfe11ea4171a6e9a577a6150f530d7120372.png";
const IMG_TOUR = "https://static.prod-images.emergentagent.com/jobs/38ff7071-66f9-4d88-af35-8aea95a1b115/images/63ab33eb3801e9a5369affb1e1b48606d5fd57f5fdc35363afabb7aaa0717ab1.png";
const IMG_MORTGAGE = "https://static.prod-images.emergentagent.com/jobs/38ff7071-66f9-4d88-af35-8aea95a1b115/images/f7240d7f0356a59aa459a4c47304d831e3fda71952f7c9fd77cd021dea2b3195.png";
const IMG_INSURANCE = "https://static.prod-images.emergentagent.com/jobs/38ff7071-66f9-4d88-af35-8aea95a1b115/images/6cfa4424488b3412ebcbcdb1a72ed1656f70259cdd537f3c3dd9e2889682149a.png";
const IMG_CLOSING = "https://static.prod-images.emergentagent.com/jobs/38ff7071-66f9-4d88-af35-8aea95a1b115/images/435702dd1212715b9ad6cf4bae9a01f359df33c4e27b9a6bab5fb93957d48e9e.png";
const IMG_HOMEOWNER = "https://static.prod-images.emergentagent.com/jobs/38ff7071-66f9-4d88-af35-8aea95a1b115/images/177bfad2ced281f2f8c806981cf5eac35554ec14eae3130119705262109454a6.png";
const IMG_HOME_LIFE = "https://static.prod-images.emergentagent.com/jobs/38ff7071-66f9-4d88-af35-8aea95a1b115/images/d3ae7b92f26a0bd7b1b80332b43dfaafe5b7b2218394ed2bd0c444d203e03b91.png";

const EmployeeExperiencePage = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const menuItems = [
    { label: "Home", action: () => navigate("/") },
    { label: "How It Works", action: () => navigate("/how-it-works") },
    { label: "Retention Programs", action: () => navigate("/retention-programs") },
    { label: "Retention Analysis", action: () => navigate("/retention-analysis") },
    { label: "Employee Experience", action: () => navigate("/employee-experience") },
    { label: "Our Story", action: () => navigate("/our-story") },
    { label: "Retention Calculator", action: () => navigate("/retention-calculator") },
    { label: "Events", action: () => navigate("/events") },
    { label: "Book a Call", action: () => navigate("/book-a-call") },
    { label: "Careers", action: () => navigate("/careers") },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="employee-experience-page">
      <SEO title="Christina's Story - Employee Experience" description="See how Christina became a homeowner through her employer's homeownership benefit. Real estate, mortgage, and insurance support all in one." />
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-100 py-2">
        <div className="container-main flex items-center justify-between py-8 md:py-10">
          <Link to="/">
            <img src={LOGO_URL} alt="CityWorth" className="h-10 md:h-12" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/get-started">
              <Button className="btn-primary text-white px-6 py-2.5">Get Started</Button>
            </Link>
            <div className="relative">
              <Button
                variant="outline"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="flex items-center gap-2 border-slate-300 px-4 py-2.5"
              >
                <Menu className="w-5 h-5" />
                <span className="hidden sm:inline">Menu</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} />
              </Button>
              {isMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsMenuOpen(false)} />
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50">
                    {menuItems.map((item, i) => (
                      <button
                        key={i}
                        onClick={() => { item.action(); setIsMenuOpen(false); }}
                        className="w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* ===== HERO: Christina's Story ===== */}
      <section className="pt-12 pb-20 bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 border border-red-100 mb-6">
                <Heart className="w-4 h-4 text-red-600" />
                <span className="text-sm font-medium text-red-700">Christina's Story</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                Opening the Door to a{" "}
                <span className="gradient-text">Better Life</span>
              </h1>
              <p className="text-xl text-slate-600 mb-4">
                Christina had a good job, a steady income, and big goals for her future.
              </p>
              <p className="text-xl text-slate-700 font-semibold mb-4">
                Owning a home was one of them.
              </p>
              <p className="text-lg text-slate-500">
                But like many employees today, she wasn't sure how she would get there.
              </p>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img src={IMG_BROWSING} alt="Christina browsing homes online" className="w-full h-[300px] lg:h-[450px] object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== The Struggle ===== */}
      <section className="py-16 bg-white">
        <div className="container-main max-w-3xl">
          <p className="text-lg text-slate-600 mb-6 leading-relaxed">
            Every time she looked at homes online, the prices felt overwhelming. She had questions about credit, down payments, loan options, and how much money she would really need. The process felt complicated and out of reach.
          </p>
          <p className="text-lg text-slate-500 mb-8 leading-relaxed">
            For a while, buying a home stayed in the category of <span className="italic">maybe someday</span>.
          </p>
          <div className="bg-gradient-to-r from-red-50 to-blue-50 rounded-2xl p-8 border border-slate-100">
            <p className="text-xl text-slate-800 font-semibold mb-2">Then something changed.</p>
            <p className="text-lg text-slate-600">
              Her company introduced a homeownership benefit designed to help employees reach one of life's biggest goals.
            </p>
          </div>
        </div>
      </section>

      {/* ===== A Team Built Around Christina ===== */}
      <section className="py-20 bg-slate-50">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img src={IMG_TEAM} alt="Christina meeting with her team" className="w-full h-[350px] object-cover" />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                A Team Built Around{" "}
                <span className="gradient-text">Christina</span>
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                One of the biggest things Christina noticed right away was that she didn't have to figure anything out on her own.
              </p>
              <p className="text-slate-600 mb-6">
                Instead of trying to coordinate lenders, agents, and insurance companies herself, she had a team working together for her.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Building, color: "bg-blue-100 text-blue-600", title: "Real Estate Agent", desc: "Helping her search for and buy the right home" },
                  { icon: DollarSign, color: "bg-green-100 text-green-600", title: "Mortgage Banker", desc: "Helping her get approved and find the best loan" },
                  { icon: Shield, color: "bg-purple-100 text-purple-600", title: "Insurance Specialist", desc: "Helping her protect the home once she bought it" },
                ].map((member, i) => (
                  <div key={i} className="flex items-start gap-4 bg-white rounded-xl p-4 border border-slate-200">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${member.color.split(' ')[0]}`}>
                      <member.icon className={`w-5 h-5 ${member.color.split(' ')[1]}`} />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">{member.title}</div>
                      <div className="text-sm text-slate-500">{member.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="max-w-3xl mx-auto mt-12">
            <p className="text-lg text-slate-600 leading-relaxed">
              What made the experience different was that the team worked together behind the scenes. They coordinated the process with each other so Christina did not have to manage everything herself. Instead of spending hours figuring out what to do next, she simply focused on finding the right home.
            </p>
          </div>
        </div>
      </section>

      {/* ===== Real Estate Process ===== */}
      <section className="py-20 bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 mb-6">
                <Building className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Real Estate</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                The Real Estate Process{" "}
                <span className="text-blue-600">Made Simple</span>
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                The first step was working with her real estate agent to understand what kind of home she wanted.
              </p>
              <p className="text-slate-600 mb-4 font-medium">Her agent helped her:</p>
              <ul className="space-y-3 mb-8">
                {[
                  "Search for homes that matched her price range and preferences",
                  "Identify neighborhoods that fit her lifestyle",
                  "Find properties as soon as they hit the market",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-slate-600 leading-relaxed">
                When it came time to see homes, the process was completely on Christina's schedule. Her agent coordinated private home tours around her availability, making it easy for her to see homes after work or on weekends.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img src={IMG_TOUR} alt="Christina touring a home" className="w-full h-[350px] object-cover" />
            </div>
          </div>
          <div className="max-w-3xl mx-auto mt-12 space-y-6">
            <p className="text-lg text-slate-600 leading-relaxed">
              As Christina found homes she liked, her agent helped her analyze the options, compare properties, and determine which home made the most sense for her long term goals.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed">
              When she found the one she loved, her agent guided her through the offer process. They helped structure a strong offer and negotiate the terms so she could secure the home at a great price.
            </p>
            <p className="text-lg text-slate-700 font-semibold text-center">
              Christina never felt like she was guessing or making decisions alone.
            </p>
          </div>
        </div>
      </section>

      {/* ===== Mortgage Guidance ===== */}
      <section className="py-20 bg-slate-50">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="rounded-2xl overflow-hidden shadow-lg order-2 lg:order-1">
              <img src={IMG_MORTGAGE} alt="Christina meeting with mortgage banker" className="w-full h-[350px] object-cover" />
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 border border-green-100 mb-6">
                <DollarSign className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-700">Mortgage</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Mortgage Guidance{" "}
                <span className="text-green-600">Every Step of the Way</span>
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                At the same time, her mortgage banker was helping on the financing side. Instead of trying to understand loan programs on her own, Christina had a professional walking her through everything.
              </p>
              <p className="text-slate-600 mb-4 font-medium">Her mortgage banker helped her:</p>
              <ul className="space-y-3">
                {[
                  "Understand how much home she could afford",
                  "Review loan programs that fit her situation",
                  "Get pre approved so she could confidently shop for homes",
                  "Structure the financing to keep her monthly payment comfortable",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="max-w-3xl mx-auto mt-12">
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Because the mortgage banker was already coordinating with the real estate agent, the process moved smoothly once Christina found a home.
            </p>
            <div className="bg-green-50 rounded-2xl p-8 border border-green-100 text-center">
              <p className="text-lg text-slate-700 mb-3">
                Even better, her employer contributed <span className="font-bold text-green-700 text-xl">$10,000</span> toward her down payment through the homeownership benefit.
              </p>
              <p className="text-slate-600 mb-4">
                Combined with the financing structure and homebuying rewards available through the program, Christina ended up bringing <span className="font-bold text-slate-900">less than $1,000</span> of her own money to closing.
              </p>
              <p className="text-lg text-green-700 font-semibold">
                Something that once felt financially impossible suddenly became achievable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Insurance Made Easy ===== */}
      <section className="py-20 bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-50 border border-purple-100 mb-6">
                <Shield className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-medium text-purple-700">Insurance</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Insurance{" "}
                <span className="text-purple-600">Made Easy</span>
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                The final step was protecting the home she worked so hard to purchase. Normally, buyers have to shop around for homeowners insurance on their own while managing everything else during the closing process.
              </p>
              <p className="text-slate-700 font-medium mb-4">
                But Christina had an insurance specialist already part of her team.
              </p>
              <p className="text-slate-600 mb-4 font-medium">The insurance expert helped her:</p>
              <ul className="space-y-3 mb-6">
                {[
                  "Compare coverage options",
                  "Find a competitive rate",
                  "Make sure the policy matched the needs of her new home",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-slate-600 leading-relaxed">
                Because the insurance specialist was coordinating with the rest of the team, everything was set up in time for closing without Christina having to chase paperwork or manage multiple conversations.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img src={IMG_INSURANCE} alt="Christina with insurance specialist" className="w-full h-[350px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* ===== The Day Christina Became a Homeowner ===== */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/10">
              <img src={IMG_CLOSING} alt="Christina at the closing table" className="w-full h-[350px] object-cover" />
            </div>
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-yellow-400/20 border border-yellow-400/30 mb-6">
                <Key className="w-4 h-4 text-yellow-400" />
                <span className="text-sm font-medium text-yellow-300">Closing Day</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                The Day Christina Became a{" "}
                <span className="text-yellow-400">Homeowner</span>
              </h2>
              <p className="text-lg text-white/80 mb-6">
                Months after first hearing about the program, Christina sat at the closing table signing the final paperwork.
              </p>
              <p className="text-xl text-white/90 font-semibold mb-6">
                What once felt overwhelming had become a clear, guided process.
              </p>
              <p className="text-xl text-yellow-400 font-bold mb-8">
                She now owned her own home.
              </p>
              <div className="space-y-3">
                {["It was stability.", "It was independence.", "It was the beginning of building long term wealth."].map((line, i) => (
                  <p key={i} className="text-lg text-white/90 flex items-center gap-3">
                    <Star className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                    {line}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Christina as Homeowner ===== */}
      <section className="py-0">
        <img src={IMG_HOMEOWNER} alt="Christina in front of her new home" className="w-full h-[350px] md:h-[500px] object-cover" />
      </section>

      {/* ===== Why This Matters ===== */}
      <section className="py-20 bg-white">
        <div className="container-main max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 text-center">
            Why This <span className="gradient-text">Matters</span>
          </h2>
          <p className="text-lg text-slate-600 mb-6 leading-relaxed">
            Many employees leave companies not because of the job itself, but because they cannot see a path to reach their personal goals.
          </p>
          <p className="text-lg text-slate-600 mb-6 leading-relaxed">
            Homeownership is one of the biggest goals people have, yet it has become harder and harder to achieve.
          </p>
          <p className="text-xl text-slate-800 font-semibold mb-6">
            This program changes that.
          </p>
          <p className="text-lg text-slate-600 leading-relaxed">
            By simplifying the process, coordinating experts into one team, and providing financial support, employees gain something powerful.
          </p>
          <p className="text-2xl text-slate-900 font-bold text-center mt-8">
            A real path to homeownership.
          </p>
        </div>
      </section>

      {/* ===== Building Wealth ===== */}
      <section className="py-20 bg-slate-50">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Building Wealth{" "}
                <span className="gradient-text">for the Future</span>
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                For everyday Americans, homeownership remains the number one way families build wealth over time.
              </p>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                With every payment, homeowners build equity in an asset that can support their long term financial future and retirement.
              </p>
              <p className="text-lg text-slate-700 font-medium mb-4">
                For Christina, buying her home was not just about today.
              </p>
              <p className="text-xl text-slate-800 font-semibold">
                It was about creating stability and building wealth for the years ahead.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img src={IMG_HOME_LIFE} alt="Christina relaxing in her new home" className="w-full h-[350px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-600 via-red-700 to-blue-800" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-yellow-400/20 via-transparent to-transparent" />
        <div className="container-main relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Making Homebuying Easier and More Affordable
            </h2>
            <p className="text-xl text-white/90 mb-6">
              Through the partnership between employers and CityWorth, employees receive the support, guidance, and financial resources needed to become homeowners.
            </p>
            <p className="text-lg text-white/80 mb-10">
              Programs like this help employees reach one of life's biggest milestones.
            </p>
            <p className="text-2xl font-bold text-yellow-400 mb-10">
              Opening the door to a better life and making homebuying easier and more affordable for employees.
            </p>
            <Link to="/get-started">
              <Button
                size="lg"
                className="bg-yellow-400 hover:bg-yellow-300 text-slate-900 px-10 py-7 text-xl font-bold shadow-2xl shadow-yellow-400/30 hover:shadow-yellow-400/50 transition-all hover:scale-105"
              >
                Get Started
                <ArrowRight className="ml-2 w-6 h-6" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <div className="bg-slate-50 border-t border-slate-200 py-6">
        <div className="container-main">
          <p className="text-sm text-slate-400 text-center italic max-w-2xl mx-auto">
            Christina is not a real person. Her story is based on the experiences of many different employees who have gone through something very similar with this program.
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-12 bg-white border-t border-slate-200">
        <div className="container-main flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/">
            <img src={LOGO_URL} alt="CityWorth" className="h-8" />
          </Link>
          <div className="flex flex-wrap items-center justify-center gap-6">
            <Link to="/" className="text-slate-600 hover:text-slate-900">Home</Link>
            <Link to="/how-it-works" className="text-slate-600 hover:text-slate-900">How It Works</Link>
            <Link to="/retention-programs" className="text-slate-600 hover:text-slate-900">Programs</Link>
            <Link to="/employee-experience" className="text-slate-600 hover:text-slate-900">Employee Experience</Link>
            <Link to="/events" className="text-slate-600 hover:text-slate-900">Events</Link>
          </div>
          <p className="text-slate-500 text-sm">&copy; {new Date().getFullYear()} CityWorth</p>
        </div>
      </footer>
    </div>
  );
};

export default EmployeeExperiencePage;
